<template>
    <pc-interact
        class="orderable-wrapper position-relative"
        v-bind:class="outerClassObject"
        v-bind:style="outerStyleObject"
        is-draggable
        v-bind:auto-scroll="autoScroll"
        v-on:drag-start="dragStart"
        v-on:drag-move="dragMove"
        v-on:drag-end="dragEnd">
        <component
            v-bind:is="listComponent"
            data-test-role="orderable-list-component"
            type="transition"
            name="list"
            class="position-relative d-flex w-100"
            v-bind:class="listClassObject"
            tag="div">
            <template
                v-for="itemKey in internalItemKeys"
                v-bind:key="itemKey">
                <template v-if="itemKey === PLACEHOLDER_KEY">
                    <placeholder
                        ref="placeholder"
                        class="list-item"
                        data-test-role="orderable-placeholder"
                        v-bind:class="placeHolderItemClassObject"
                        v-bind:width="placeholderWidth"
                        v-bind:height="placeholderHeight"
                        v-bind:external-accept="itemAcceptedExternally" />
                </template>
                <template v-else>
                    <orderable-item
                        ref="orderables"
                        v-slot:default="{ itemKey: slotItemKey }"
                        v-bind:item-key="itemKey"
                        v-bind:horizontal="horizontal"
                        v-bind:show-arrows="_showArrows"
                        v-bind:show-add-remove="_showAddRemove"
                        v-bind:show-item-shadow-box="showItemShadowBox"
                        v-bind:data-item-key="itemKey"
                        class="list-item"
                        v-bind:class="itemClassObject"
                        v-bind:read-only="readOnly"
                        v-on:prev="prev"
                        v-on:next="next"
                        v-on:del="del"
                        v-on:add="add">
                        <slot
                            v-bind:item-key="slotItemKey"
                            v-bind:delete-hook="del" />
                    </orderable-item>
                </template>
            </template>
        </component>
        <pc-transition
            v-if="showEmptyContent"
            class="position-absolute empty-content-wrapper">
            <slot name="emptyContent" />
        </pc-transition>
    </pc-interact>
</template>

<script>
    import { TransitionGroup } from "vue";
    import orderableItem from "./_orderable-item.vue";
    import placeholder from "./_placeholder.vue";
    import { ORDERABLE_HUB_MIXIN, eventIsInsideRect, createOrderableHub } from "./_util";

    const PLACEHOLDER_KEY = "PLACEHOLDER_KEY";
    export { createOrderableHub };

    export default {
        components: { orderableItem, placeholder, TransitionGroup },
        mixins: [ORDERABLE_HUB_MIXIN],
        props: {
            modelValue: { type: Array, required: true },
            horizontal: { type: Boolean, required: false, default: false },
            orderableHub: { type: Object, required: false, default: null },
            showAddRemove: { type: Boolean, required: false, default: false },
            showArrows: { type: Boolean, required: false, default: false },
            showItemShadowBox: { type: Boolean, required: false, default: true },
            allowRemoveHook: { type: Function, required: false, default: null },
            addItemKeyHook: { type: Function, required: false, default: null },
            spacingClassObject: { type: Object, required: false, default: null },
            placeHolderClassObject: { type: Object, required: false, default: null },
            itemBorderClassObject: { type: Object, required: false, default: null },
            listComponentClassObject: { type: Object, required: false, default: null },
            readOnly: { type: Boolean, required: false, default: false },
            autoScroll: { type: [Boolean, Object], required: false, default: true },
            // This is used by the ORDERABLE_HUB_MIXIN
            externalItemValidator: { type: Function, required: false, default: null },
            selectedItemKeys: { type: Array, required: false, default: () => [] }
        },
        emits: ["drag-start", "drag-end", "update:modelValue", "list-component-changed"],
        data: function () {
            return {
                internalItemKeys: [...this.modelValue],
                dragItemKey: null,
                draggingStartRect: null,
                PLACEHOLDER_KEY,
                orderable: null,
                dragJustEnded: false
            };
        },
        computed: {
            itemClassObject() {
                return { ...this.spacingClassObject, ...this.itemBorderClassObject };
            },
            placeHolderItemClassObject() {
                return { ...this.spacingClassObject, ...this.placeHolderClassObject };
            },
            _showArrows() {
                return this.showArrows && !this.readOnly;
            },
            _showAddRemove() {
                return Boolean(this.showAddRemove && typeof this.addItemKeyHook === "function") && !this.readOnly;
            },
            listComponent() {
                return this.internalItemKeys.length > 100 ? "div" : "TransitionGroup";
            },
            placeholderWidth() {
                if (this.draggingStartRect) {
                    return this.draggingStartRect.width;
                }
                if (this.orderableHub && this.orderableHub.dragRect) {
                    return this.orderableHub.dragRect.width;
                }
                return 0;
            },
            placeholderHeight() {
                if (this.draggingStartRect) {
                    return this.draggingStartRect.height;
                }
                if (this.orderableHub && this.orderableHub.dragRect) {
                    return this.orderableHub.dragRect.height;
                }
                return 0;
            },
            listClassObject() {
                return {
                    "flex-wrap": this.horizontal,
                    "flex-column": !this.horizontal,
                    ...this.listComponentClassObject
                };
            },
            outerClassObject() {
                return {
                    "border-primary": this.externalItemAcceptable
                };
            },
            outerStyleObject() {
                const style = {
                    transition: "all .4s"
                };
                if (this.externalItemAcceptable) {
                    style.borderStyle = "solid";
                    style.borderWidth = "3px";
                } else {
                    style.borderStyle = "";
                }
                if (this.acceptExternalItem) {
                    style.borderStyle = "dashed";
                }
                return style;
            },
            dragItemIndex() {
                return this.getItemIndex(this.dragItemKey);
            },
            placeholderIndex() {
                return this.getItemIndex(this.PLACEHOLDER_KEY);
            },
            showEmptyContent() {
                return !this.internalItemKeys || this.internalItemKeys.length < 1;
            }
        },
        watch: {
            modelValue: {
                handler() {
                    this.internalItemKeys = [...this.modelValue];
                },
                deep: true
            },
            listComponent() {
                this.$emit("list-component-changed");
            }
        },
        created() {
            if (this.showAddRemove && typeof this.addItemKeyHook !== "function") {
                console.warn("pc-orderable buttons disabled. Need to specify an addItemKeyHook function.");
            }
        },
        methods: {
            dragStart(dragEvent) {
                if (this.readOnly) {
                    return;
                }
                if (!this.$refs.orderables) {
                    return; // empty orderable shouldn't pick up drag events
                }
                for (let i = 0; i < this.$refs.orderables.length; i++) {
                    const orderable = this.$refs.orderables[i];
                    if (orderable.isInside(dragEvent)) {
                        this.orderable = orderable;
                        this.orderable.dragStart();
                        this.draggingStartRect = this.orderable.rect;
                        this.dragItemKey = this.orderable.itemKey;
                        // Add placeholder at selected index
                        this.internalItemKeys.splice(this.dragItemIndex, 0, this.PLACEHOLDER_KEY);
                        break;
                    }
                }
                dragEvent.dragItemKey = this.dragItemKey;
                this.$emit("drag-start", dragEvent);
            },
            async dragMove(dragEvent) {
                if (this.orderable) {
                    this.orderable.dragMove(dragEvent);

                    //give the transition mechanism a chance to finish up, otherwise we can get timing based weirdness
                    await this.$nextTick();
                    this.orderableHubDragMove(dragEvent);
                    const rect = this.$el.getBoundingClientRect();
                    if (!eventIsInsideRect(rect, dragEvent)) {
                        return;
                    }

                    this.doDragMove(this.dragItemKey, dragEvent);
                }
            },
            dragEnd(dragEvent) {
                this.$emit("drag-end", dragEvent);
                if (this.orderable) {
                    this.dragJustEnded = true;
                    this.orderable.dragEnd();
                    this.orderable = null;

                    const areMultipleDragItems =
                        this.selectedItemKeys.length > 1 && this.selectedItemKeys.includes(this.dragItemKey);
                    const dragChangeDetails = areMultipleDragItems
                        ? this.moveSelectedItemsAndBuildDetails()
                        : this.moveDragItemAndBuildDetails();

                    setTimeout(() => {
                        this.dragJustEnded = false;
                    }, 100);

                    this.$emit("update:modelValue", this.internalItemKeys, dragChangeDetails);

                    this.orderableHubDrop(dragEvent);
                    this.dragItemKey = null;
                }
            },
            moveSelectedItemsAndBuildDetails() {
                const sortedSelectedItemKeys = [...this.selectedItemKeys].sort((a, b) => {
                    const indexA = this.internalItemKeys.indexOf(a);
                    const indexB = this.internalItemKeys.indexOf(b);
                    return indexA - indexB;
                });

                for (const key of sortedSelectedItemKeys) {
                    const index = this.getItemIndex(key);
                    if (index >= 0) {
                        this.internalItemKeys.splice(index, 1);
                    }
                }

                const targetIndex = this.placeholderIndex;
                this.internalItemKeys.splice(this.placeholderIndex, 1, ...sortedSelectedItemKeys);

                return {
                    targetIndex,
                    sortedKeys: sortedSelectedItemKeys
                };
            },
            moveDragItemAndBuildDetails() {
                this.internalItemKeys.splice(this.dragItemIndex, 1);
                const targetIndex = this.placeholderIndex;
                this.internalItemKeys.splice(this.placeholderIndex, 1, this.dragItemKey);

                return {
                    targetIndex,
                    sortedKeys: [this.dragItemKey]
                };
            },
            prev(itemKey) {
                if (this.readOnly) {
                    return;
                }
                const index = this.getItemIndex(itemKey);
                if (index < 1) {
                    return;
                }
                const removed = this.internalItemKeys.splice(index, 1);
                this.internalItemKeys.splice(index - 1, 0, removed[0]);
                this.$emit("update:modelValue", this.internalItemKeys);
            },
            next(itemKey) {
                if (this.readOnly) {
                    return;
                }
                const index = this.getItemIndex(itemKey);
                if (index < 0 || index + 1 >= this.modelValue.length) {
                    return;
                }
                const removed = this.internalItemKeys.splice(index, 1);
                this.internalItemKeys.splice(index + 1, 0, removed[0]);
                this.$emit("update:modelValue", this.internalItemKeys);
            },
            async del(itemKey) {
                if (this.readOnly) {
                    return;
                }
                const index = this.getItemIndex(itemKey);
                if (index < 0) {
                    return;
                }
                if (!(await this.getAllowRemove())) {
                    return;
                }
                this.internalItemKeys.splice(index, 1);
                this.$emit("update:modelValue", this.internalItemKeys);
            },
            async add(itemKey) {
                if (this.readOnly) {
                    return;
                }
                const index = this.getItemIndex(itemKey);
                if (index < 0) {
                    return;
                }
                if (typeof this.addItemKeyHook === "function") {
                    const result = await this.addItemKeyHook(index);
                    if (result) {
                        this.internalItemKeys.splice(index + 1, 0, result);
                        this.$emit("update:modelValue", this.internalItemKeys);
                    }
                }
            },
            async getAllowRemove() {
                if (this.allowRemoveHook) {
                    const result = await this.allowRemoveHook();
                    return Boolean(result);
                }
                return true;
            },
            doDragMove(itemKey, dragEvent) {
                if (itemKey === null) {
                    return;
                }
                if (!this.$refs.orderables) {
                    return;
                }

                for (let i = 0; i < this.$refs.orderables.length; i++) {
                    const orderable = this.$refs.orderables[i];
                    if (orderable.itemKey !== itemKey) {
                        if (orderable.isInside(dragEvent) && !orderable.ordering) {
                            const beforeOrAfter = orderable.getInsertPosition(dragEvent);
                            if (this.movePlaceholder(orderable, beforeOrAfter)) {
                                orderable.setOrdering();
                            }
                            return;
                        }
                    }
                }
            },
            getItemIndex(itemKey) {
                return this.internalItemKeys.indexOf(itemKey);
            },
            movePlaceholder(orderable, drop) {
                const item = orderable.itemKey;
                const index = this.getItemIndex(item);
                if (index < 0 || index === this.placeholderIndex) {
                    return false;
                }
                /**
                 * We need to pretend as if the selected item is not in the list, so we need to calculate index offsets
                 * for the item index and the placeholder index.
                 * Additionally, we are going to remove and then re-add the placeholder, which means we need an offset
                 * for the item index if it comes after the placeholder.
                 * Finally, if the drop happens after the item, we need a positive offset.
                 */
                const indexSelectedOffset = index > this.dragItemIndex ? -1 : 0;
                const placeholderSelectedOffset = this.placeholderIndex > this.dragItemIndex ? -1 : 0;
                const indexPlaceholderOffset = index > this.placeholderIndex ? -1 : 0;
                const dropOffset = drop === "after" ? 1 : 0;

                //make sure we don't do anything if the before and after states are equivalent
                if (
                    index + indexSelectedOffset + indexPlaceholderOffset + dropOffset ===
                    this.placeholderIndex + placeholderSelectedOffset
                ) {
                    return false;
                }

                this.internalItemKeys.splice(this.placeholderIndex, 1);
                this.internalItemKeys.splice(index, 0, this.PLACEHOLDER_KEY);

                return true;
            },
            getOrderableComponents() {
                return this.$refs.orderables;
            }
        }
    };
</script>
<style lang="scss" scoped>
    .orderable-wrapper {
        border-radius: var(--pc-input-border-radius);
    }
    .list-item {
        position: relative;
        transition-property: transform;
        transition-duration: 0.4s;
    }
    .list-enter-from,
    .list-leave-to,
    .list-leave-from {
        opacity: 0;
    }
    .list-leave-active {
        position: absolute;
        transition-duration: 0s;
    }
    .empty-content-wrapper {
        top: 0px;
        left: 0px;
        height: 100%;
        width: 100%;
    }
</style>
